<template>
  <div
    v-if="elementAbove.length"
    :data-tint="type"
    class="relative z-90 flex animate-small-bounce-reverse flex-row items-center rounded bg-primary px-3 py-1.5 text-contrast-primary shadow-2xl shadow-secondary-900"
    :style="styleAbove"
  >
    <div
      class="absolute right-2 z-60 h-3 w-3 -translate-y-3 rotate-45 bg-primary"
    ></div>
    <div class="flex flex-row items-center gap-1.5">
      <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" />
      <span class="font-bold">
        {{ elementAbove.length }}
        {{ $t(elementAbove.length > 1 ? 'global.errors' : 'global.error') }}
      </span>
    </div>
  </div>
  <div
    v-if="elementUnder.length"
    :data-tint="type"
    class="z-90 flex animate-small-bounce flex-row items-center rounded bg-primary px-3 py-1.5 text-contrast-primary shadow"
    :style="styleUnder"
  >
    <div
      class="absolute bottom-0 right-2 z-60 h-3 w-3 translate-y-1 rotate-45 bg-primary"
    ></div>
    <div class="flex flex-row items-center gap-1.5">
      <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" />
      <span class="font-bold">
        {{ elementUnder.length }}
        {{ $t(elementUnder.length > 1 ? 'global.errors' : 'global.error') }}
      </span>
    </div>
  </div>
  <div>
    <slot name="default"></slot>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'FormValidationObserver',
  components: { FontAwesomeIcon },
  props: {
    rootId: {
      type: String,
      required: true
    },
    marginTop: {
      type: Number,
      required: false,
      default: 0
    },
    marginBottom: {
      type: Number,
      required: false,
      default: 0
    },
    overideMetadataType: {
      type: Boolean,
      required: false,
      default: false
    },
    type: {
      type: String,
      required: false,
      default: 'danger'
    }
  },
  data() {
    return {
      rootElement: null,
      rootElementBoundary: null,
      elementErrorHelper: [],
      intersectionObserver: null,
      mutationObserver: null,
      mutationCollectionObserver: null,
      resizeObserver: null,
      elementAbove: [],
      elementUnder: [],
      elementInside: []
    }
  },
  computed: {
    styleAbove() {
      return {
        position: 'fixed',
        top: this.rootElement
          ? this.rootElementBoundary.top + 5 + 'px'
          : '-999px',
        left: this.rootElement
          ? this.rootElementBoundary.left + 30 + 'px'
          : '-999px'
      }
    },
    styleUnder() {
      return {
        position: 'fixed',
        top: this.rootElement
          ? this.rootElementBoundary.top +
            this.rootElementBoundary.height -
            45 +
            'px'
          : '-999px',
        left: this.rootElement
          ? this.rootElementBoundary.left + 30 + 'px'
          : '-999px'
      }
    }
  },
  watch: {
    $route(newValue, oldValue) {
      if (newValue.meta.display_type === 'view') {
        this.elementInside = []
        this.elementAbove = []
        this.elementUnder = []
        this.mutationObserver.disconnect()
        this.mutationCollectionObserver.disconnect()
        this.intersectionObserver.disconnect()
      } else if (newValue.meta.display_type === 'edit') {
        this.initMutationObserver()
      }
    }
  },
  created() {
    this.resizeObserver = new ResizeObserver((entries) => {
      this.rootElementBoundary = this.rootElement.getBoundingClientRect()
    })
    this.mutationObserver = new MutationObserver(this.onMutationObserved, {
      subtree: true,
      childList: true
    })
    this.mutationCollectionObserver = new MutationObserver(
      this.onMutationCollectionObserved,
      {
        childList: true
      }
    )
  },
  unmounted() {
    window.removeEventListener('resize', this.windowResized)
  },
  beforeUnmount() {
    this.elementInside = []
    this.elementAbove = []
    this.elementUnder = []
    this.intersectionObserver.disconnect()
    this.mutationObserver.disconnect()
    this.mutationCollectionObserver.disconnect()
    this.resizeObserver.disconnect()
  },
  mounted() {
    this.rootElement = document.getElementById(this.rootId)
    this.rootElementBoundary = this.rootElement.getBoundingClientRect()
    window.addEventListener('resize', this.windowResized)
    this.intersectionObserver = new IntersectionObserver(
      this.onElementObserved,
      {
        root: this.rootElement,
        threshold: 0
      }
    )
    if (this.overideMetadataType || this.$route.meta.display_type === 'edit') {
      this.initMutationObserver()
    }
  },
  methods: {
    onMutationObserved(entries) {
      this.elementInside = []
      this.elementAbove = []
      this.elementUnder = []
      this.intersectionObserver.disconnect()
      entries.forEach((entry) => {
        if (entry.target.children.length) {
          this.intersectionObserver.observe(entry.target)
        }
      })
    },
    onElementObserved(entries) {
      entries.forEach((entry) => {
        if (entry.target.childNodes.length) {
          if (!entry.isIntersecting) {
            if (entry.boundingClientRect.y < entry.rootBounds.y) {
              this.elementAbove.push(entry.target)
            } else {
              this.elementUnder.push(entry.target)
            }
          } else {
            this.elementAbove = this.elementAbove.filter(
              (element) => element !== entry.target
            )
            this.elementUnder = this.elementUnder.filter(
              (element) => element !== entry.target
            )
          }
        }
      })
    },
    onMutationCollectionObserved(entries) {
      entries.forEach((entry) => {
        if (
          Array.from(entry.addedNodes).some(
            (node) =>
              node.nodeType === 1 && node.tagName.toLowerCase() === 'div'
          )
        ) {
          this.observeElementInSpecificParent(entry.target)
        }
      })
    },
    windowResized() {
      this.rootElementBoundary = this.rootElement.getBoundingClientRect()
    },
    observeElementInSpecificParent(parentElement) {
      parentElement
        .querySelectorAll('.form-help-container:not([data-observed])')
        .forEach((element) => {
          this.mutationObserver.observe(element, {
            childList: true,
            subtree: true
          })
          element.setAttribute('observed', true)
        })
    },
    initMutationObserver() {
      setTimeout(() => {
        this.resizeObserver.observe(this.rootElement)
        this.observeElementInSpecificParent(this.rootElement)
        this.rootElement
          .querySelectorAll('.collection-container')
          .forEach((element) => {
            this.mutationCollectionObserver.observe(element, {
              childList: true
            })
          })
      }, 1000)
    }
  }
}
</script>
